import type { AssetListItem, ViewerKind } from "@/lib/api";

const FORMAT_BY_EXT: Record<string, string> = {
  ".pdf": "PDF",
  // Illustrator (set/2026): entra como PDF no servidor, mas o rótulo diz o
  // que a pessoa enviou — a extensão vem antes do MIME de propósito.
  ".ai": "AI",
  ".jpg": "JPEG",
  ".jpeg": "JPEG",
  ".png": "PNG",
  ".tif": "TIFF",
  ".tiff": "TIFF",
};

const FORMAT_BY_MIME: Record<string, string> = {
  "application/pdf": "PDF",
  "application/postscript": "AI",
  "application/illustrator": "AI",
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "image/tiff": "TIFF",
};

export function assetFormatLabel(asset: AssetListItem): string {
  const ext = String(asset.source_ext ?? "").trim().toLowerCase();
  if (ext && FORMAT_BY_EXT[ext]) return FORMAT_BY_EXT[ext];

  const mime = String(asset.source_mime ?? "").trim().toLowerCase();
  if (mime && FORMAT_BY_MIME[mime]) return FORMAT_BY_MIME[mime];

  const fallbackExt = asset.filename.match(/\.[^.]+$/)?.[0]?.toLowerCase();
  if (fallbackExt && FORMAT_BY_EXT[fallbackExt]) return FORMAT_BY_EXT[fallbackExt];

  return String(asset.source_type ?? "").toLowerCase() === "raster" ? "Imagem" : "Arquivo";
}

export function assetHasChannels(asset: AssetListItem): boolean {
  return Boolean(asset.has_channels) || Boolean((asset.channel_count ?? 0) > 0) || asset.plates.length > 0;
}

/**
 * O selo "Com Canais" só informa algo quando é exceção.
 *
 * Todo PDF prepress renderiza com separação, então o selo não distingue nada
 * e vira ruído em cima do selo de formato. Em raster ele continua valendo:
 * uma imagem com canais é o caso incomum.
 */
export function assetShowsChannelBadge(asset: AssetListItem): boolean {
  return assetHasChannels(asset) && assetFormatLabel(asset) !== "PDF";
}

/**
 * O miolo que o arquivo abre — o selo `Doc | Package` ao lado do formato.
 *
 * Ausente em payload antigo é embalagem: o mesmo lado seguro que o backend
 * escolhe sem veredito da triagem (`document_triage.is_document`).
 */
export function assetViewerKind(asset: AssetListItem): ViewerKind {
  return asset.viewer_kind === "document" ? "document" : "packaging";
}

export function assetDpiDetail(asset: AssetListItem): string | null {
  const sourceType = String(asset.source_type ?? "").toLowerCase();
  if (sourceType === "raster") {
    return asset.source_dpi ? `Fonte ${asset.source_dpi} DPI` : "Pixels nativos";
  }
  return asset.render_dpi ? `Render ${asset.render_dpi} DPI` : null;
}


/**
 * O último segmento de um `folder_path` — o nome do job.
 *
 * É o rótulo do card de conjunto: a listagem colapsa as N separações numa
 * entrada só (F-T.8, §4.1), e o nome de uma das tintas não descreve o que o
 * card abre.
 */
/**
 * O último segmento de um caminho de pasta.
 *
 * Sem uso na listagem desde que o produto decidiu que os sete arquivos do
 * conjunto ficam visíveis (`F-T.0-plano.md`, §15) — cada card voltou a se
 * chamar pelo arquivo. Fica de pé porque a F-T.9 vai precisar exatamente
 * disto para o **nome da peça**.
 */
export function folderName(folderPath: string): string {
  const parts = folderPath.split("/").filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : "";
}
